import { AfterViewInit, Directive, ElementRef, HostListener, Input, OnChanges, OnDestroy } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MoneyHelper } from '../services/money-helper.service';

/**
 * Display mask for a number input that always stays an input (the legacy forms): while the field is
 * not focused it paints the model at `appNumberMask` fraction digits — the organisation's Decimal
 * Places — and on focus it shows the stored value in full for editing. The model is never touched:
 * only what the box paints changes, the way a transaction-pattern cell paints a mask and its editor
 * the stored value. `null` / `undefined` paints the stored value as it is (percentages, rates).
 *
 * Number inputs reject grouped text ("1,234.50" would blank the box), so the mask pads decimals only.
 */
@Directive({
  selector: 'input[appNumberMask]'
})
export class NumberMaskDirective implements AfterViewInit, OnChanges, OnDestroy {

  @Input('appNumberMask') decimals?: number | null;

  private focused = false;
  private changes?: Subscription;

  constructor(
    private host: ElementRef<HTMLInputElement>,
    private control: NgControl,
    private moneyHelper: MoneyHelper
  ) { }

  ngAfterViewInit(): void {
    // Model writes made while the box is not focused (initial value, resets from the form) are repainted
    this.changes = this.control.valueChanges?.subscribe(() => { if (!this.focused) this.paint(); });
    this.paint();
  }

  ngOnChanges(): void {
    if (!this.focused) this.paint();
  }

  ngOnDestroy(): void {
    this.changes?.unsubscribe();
  }

  /** Runs before the template's own `(focus)` handlers, so a `select()` there selects the full value */
  @HostListener('focus')
  onFocus(): void {
    this.focused = true;
    const value = this.control.value;
    if (this.isNumber(value)) this.host.nativeElement.value = String(value);
  }

  @HostListener('blur')
  onBlur(): void {
    this.focused = false;
    this.paint();
  }

  private paint(): void {
    const value = this.control.value;
    if (!this.isNumber(value)) return;
    const decimals = this.decimals;
    this.host.nativeElement.value = decimals === null || decimals === undefined
      ? String(value)
      : this.moneyHelper.round(+value, decimals).toFixed(decimals);
  }

  private isNumber(value: unknown): value is number | string {
    return value !== null && value !== undefined && value !== '' && !isNaN(+value);
  }
}
